import { useState, useEffect } from 'react';
import { getActiveStaff } from '../../firebase/staffService';

function HairRecordForm({ clientId, clientName, existingRecord, onSave, onCancel, isClient = false }) {
  const [formData, setFormData] = useState({
    hasAlopecia: false,
    pregnantLast6Months: false,
    hasPsoriasisEczema: false,
    frequentGymOrSwim: false,
    allergies: '',
    medications: '',
    colorsHair: false,
    colorFrequency: '',
    hadSensitivityReaction: false,
    sensitivityHowLong: '',
    hasChemicalTreatments: '',
    alwaysProfessionallyTreated: false,
    usedExtensionsBefore: false,
    extensionMethod: '',
    hairType: '',
    hairTexture: '',
    hairLength: '',
    hairCondition: '',
    scalpCondition: '',
    diagnosticTest: '',
    notes: '',
    stylistOrTherapistName: isClient ? 'Client Self-Signed' : '',
  });
  
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isClient) {
      loadStaff();
    }
  }, []);

  useEffect(() => {
    if (existingRecord) {
      setFormData(existingRecord);
    }
  }, [existingRecord]);

  const loadStaff = async () => {
    const allStaff = await getActiveStaff();
    const hairStaff = allStaff.filter(s => 
      s.type === 'Caucasian Stylist' || s.type === 'Ethnic Stylist'
    );
    setStaffList(hairStaff);
  };

  const handleYesNo = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTextChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (e) => {
    setFormData(prev => ({ ...prev, stylistOrTherapistName: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isClient && !formData.stylistOrTherapistName) {
      alert('Please select a stylist');
      return;
    }
    setSaving(true);
    await onSave(formData, formData.stylistOrTherapistName);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Medical History Section */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Medical History</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <span className="w-64">Have you ever suffered from alopecia?</span>
            <button type="button" onClick={() => handleYesNo('hasAlopecia', true)} className={`px-4 py-1 rounded ${formData.hasAlopecia === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
            <button type="button" onClick={() => handleYesNo('hasAlopecia', false)} className={`px-4 py-1 rounded ${formData.hasAlopecia === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-64">Have you been pregnant in the last 6 months?</span>
            <button type="button" onClick={() => handleYesNo('pregnantLast6Months', true)} className={`px-4 py-1 rounded ${formData.pregnantLast6Months === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
            <button type="button" onClick={() => handleYesNo('pregnantLast6Months', false)} className={`px-4 py-1 rounded ${formData.pregnantLast6Months === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-64">Do you suffer from psoriasis or eczema on the scalp?</span>
            <button type="button" onClick={() => handleYesNo('hasPsoriasisEczema', true)} className={`px-4 py-1 rounded ${formData.hasPsoriasisEczema === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
            <button type="button" onClick={() => handleYesNo('hasPsoriasisEczema', false)} className={`px-4 py-1 rounded ${formData.hasPsoriasisEczema === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-64">Do you frequently go to the gym or swim?</span>
            <button type="button" onClick={() => handleYesNo('frequentGymOrSwim', true)} className={`px-4 py-1 rounded ${formData.frequentGymOrSwim === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
            <button type="button" onClick={() => handleYesNo('frequentGymOrSwim', false)} className={`px-4 py-1 rounded ${formData.frequentGymOrSwim === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Have you any known allergies?</label>
          <textarea name="allergies" value={formData.allergies} onChange={handleTextChange} rows={2} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="If yes, please state them"></textarea>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">Do you take any medication?</label>
          <textarea name="medications" value={formData.medications} onChange={handleTextChange} rows={2} className="mt-1 w-full px-3 py-2 border rounded-md" placeholder="If yes, please state them"></textarea>
        </div>
      </div>

      {/* Hair History Section */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Hair History</h3>
        <div className="flex items-center gap-4 mb-3">
          <span className="w-64">Do you currently colour your hair?</span>
          <button type="button" onClick={() => handleYesNo('colorsHair', true)} className={`px-4 py-1 rounded ${formData.colorsHair === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
          <button type="button" onClick={() => handleYesNo('colorsHair', false)} className={`px-4 py-1 rounded ${formData.colorsHair === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
        </div>
        {formData.colorsHair && (
          <div className="ml-16 mb-3">
            <label className="block text-sm font-medium text-gray-700">How often?</label>
            <input type="text" name="colorFrequency" value={formData.colorFrequency} onChange={handleTextChange} className="mt-1 px-3 py-2 border rounded-md w-64" />
          </div>
        )}
        <div className="flex items-center gap-4 mb-3">
          <span className="w-64">Ever had sensitivity or allergic reaction to hair colour?</span>
          <button type="button" onClick={() => handleYesNo('hadSensitivityReaction', true)} className={`px-4 py-1 rounded ${formData.hadSensitivityReaction === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
          <button type="button" onClick={() => handleYesNo('hadSensitivityReaction', false)} className={`px-4 py-1 rounded ${formData.hadSensitivityReaction === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
        </div>
        {formData.hadSensitivityReaction && (
          <div className="ml-16 mb-3">
            <label className="block text-sm font-medium text-gray-700">How long ago?</label>
            <input type="text" name="sensitivityHowLong" value={formData.sensitivityHowLong} onChange={handleTextChange} className="mt-1 px-3 py-2 border rounded-md w-64" />
          </div>
        )}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700">Do you currently have any Keratin, chemical or other treatments on your hair?</label>
          <textarea name="hasChemicalTreatments" value={formData.hasChemicalTreatments} onChange={handleTextChange} rows={2} className="mt-1 w-full px-3 py-2 border rounded-md"></textarea>
        </div>
        <div className="flex items-center gap-4 mb-3">
          <span className="w-64">Have you always had your hair chemically treated by a professional?</span>
          <button type="button" onClick={() => handleYesNo('alwaysProfessionallyTreated', true)} className={`px-4 py-1 rounded ${formData.alwaysProfessionallyTreated === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
          <button type="button" onClick={() => handleYesNo('alwaysProfessionallyTreated', false)} className={`px-4 py-1 rounded ${formData.alwaysProfessionallyTreated === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
        </div>
        <div className="flex items-center gap-4 mb-3">
          <span className="w-64">Have you used hair extensions before?</span>
          <button type="button" onClick={() => handleYesNo('usedExtensionsBefore', true)} className={`px-4 py-1 rounded ${formData.usedExtensionsBefore === true ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>Yes</button>
          <button type="button" onClick={() => handleYesNo('usedExtensionsBefore', false)} className={`px-4 py-1 rounded ${formData.usedExtensionsBefore === false ? 'bg-salon-button text-white' : 'bg-gray-200'}`}>No</button>
        </div>
        {formData.usedExtensionsBefore && (
          <div className="ml-16 mb-3">
            <label className="block text-sm font-medium text-gray-700">What method?</label>
            <input type="text" name="extensionMethod" value={formData.extensionMethod} onChange={handleTextChange} className="mt-1 px-3 py-2 border rounded-md w-64" />
          </div>
        )}
      </div>

      {/* Hair Characteristics */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="text-lg font-semibold mb-4">Hair Characteristics</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select name="hairType" value={formData.hairType} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Fine">Fine</option>
              <option value="Medium">Medium</option>
              <option value="Thick">Thick</option>
              <option value="Very Thick">Very Thick</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Texture</label>
            <select name="hairTexture" value={formData.hairTexture} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Curly">Curly</option>
              <option value="Straight">Straight</option>
              <option value="Wavy">Wavy</option>
              <option value="Frizzy">Frizzy</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Length</label>
            <select name="hairLength" value={formData.hairLength} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Short">Short</option>
              <option value="Medium">Medium</option>
              <option value="Long">Long</option>
              <option value="Extra Long">Extra Long</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hair Condition</label>
            <select name="hairCondition" value={formData.hairCondition} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Dry">Dry</option>
              <option value="Greasy">Greasy</option>
              <option value="Normal">Normal</option>
              <option value="Chemically Damaged">Chemically Damaged</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Scalp Condition</label>
            <select name="scalpCondition" value={formData.scalpCondition} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Oily">Oily</option>
              <option value="Flaky">Flaky</option>
              <option value="Dandruff">Dandruff</option>
              <option value="Dry">Dry</option>
              <option value="Normal">Normal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Diagnostic Test</label>
            <select name="diagnosticTest" value={formData.diagnosticTest} onChange={handleTextChange} className="mt-1 w-full px-3 py-2 border rounded-md">
              <option value="">Select</option>
              <option value="Skin">Skin</option>
              <option value="Porosity">Porosity</option>
              <option value="Colour">Colour</option>
              <option value="Pre-perm">Pre-perm</option>
              <option value="Strand">Strand</option>
              <option value="Elasticity">Elasticity</option>
              <option value="Incompatibility">Incompatibility</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Extra Remarks</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleTextChange}
          rows={3}
          className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button"
          placeholder="Any additional notes or remarks about the client's hair, preferences, or special instructions..."
        ></textarea>
        <p className="text-xs text-gray-400 mt-1">Optional – for internal use only</p>
      </div>

      {/* Stylist selection - only show for staff */}
      {!isClient && (
        <div className="bg-white rounded-lg border p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Stylist Name</label>
          <select 
            value={formData.stylistOrTherapistName} 
            onChange={handleSelectChange}
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button"
            required
          >
            <option value="">Select stylist</option>
            {staffList.map(staff => (
              <option key={staff.id} value={staff.name}>{staff.name} ({staff.type})</option>
            ))}
          </select>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300">
          Cancel
        </button>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-salon-button text-white rounded-md hover:bg-salon-button-hover disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Hair Record'}
        </button>
      </div>
    </form>
  );
}

export default HairRecordForm;